import React from 'react';
import { useNavigate } from 'react-router-dom';
import DrawerPanel from './DrawerPanel';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, Hash, TrendingUp, TrendingDown, Minus, Sparkles,
  AlertTriangle, ShieldCheck, Activity, ArrowUpRight,
} from 'lucide-react';
import { motion } from 'framer-motion';

// SeverityRibbon — top accent stripe that varies per record
const SEV_RIBBON = {
  critical: 'from-apex-critical via-apex-critical/60 to-transparent',
  high:     'from-apex-warning via-apex-warning/60 to-transparent',
  medium:   'from-sky-500 via-sky-500/50 to-transparent',
  low:      'from-emerald-500 via-emerald-500/50 to-transparent',
  enforced: 'from-apex-lens via-apex-lens/60 to-transparent',
  awareness:'from-slate-400 via-slate-300 to-transparent',
};

// Verdict tone — color tokens for the optional verdict hero block + signal callout
const VERDICT_TONE = {
  positive: { tile: 'bg-emerald-50 text-emerald-700 ring-emerald-100', text: 'text-emerald-700', call: 'bg-emerald-50/60 border-emerald-100', accent: 'text-emerald-700' },
  improving:{ tile: 'bg-sky-50 text-sky-700 ring-sky-100',           text: 'text-sky-700',     call: 'bg-sky-50/70 border-sky-100',         accent: 'text-sky-700' },
  warning:  { tile: 'bg-amber-50 text-amber-700 ring-amber-100',     text: 'text-amber-700',   call: 'bg-amber-50/60 border-amber-100',     accent: 'text-amber-700' },
  critical: { tile: 'bg-rose-50 text-rose-700 ring-rose-100',        text: 'text-rose-700',    call: 'bg-rose-50/60 border-rose-100',       accent: 'text-rose-700' },
  neutral:  { tile: 'bg-slate-100 text-slate-700 ring-slate-200',    text: 'text-slate-700',   call: 'bg-slate-50 border-slate-200',         accent: 'text-slate-700' },
};

const VERDICT_ICON = {
  positive: ShieldCheck,
  improving: TrendingUp,
  warning: AlertTriangle,
  critical: AlertTriangle,
  neutral: Activity,
};

// RecordDrawer — generic elite drawer used to drill into any tabular record on the platform.
// Accepts a `record` schema:
//   {
//     idTag, title, subtitle, severity,
//     verdict?: { tone, label, sub, icon? },
//     signal?: string | { tone, body }   // interpretive callout near the top
//     meta: [{label, value, mono, tone, sub}],
//     sections: [{title, type, items|body|chart|kpis|callout, mono, icon}],
//     actions: [{label, onClick, primary, icon, testId}]
//   }
export default function RecordDrawer({ open, onOpenChange, record, testId = 'record-drawer', onPrev, onNext, position, total }) {
  if (!record) return null;
  const { idTag, title, subtitle, severity, verdict, signal, meta = [], sections = [], actions = [] } = record;
  const ribbonGrad = SEV_RIBBON[severity] ?? SEV_RIBBON.medium;

  return (
    <DrawerPanel
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      subtitle={idTag ?? subtitle}
      severity={severity}
      testId={testId}
      onPrev={onPrev}
      onNext={onNext}
      position={position}
      total={total}
      footer={
        actions.length > 0 ? (
          <>
            {actions.map((a, i) => {
              const Icon = a.icon ?? ArrowRight;
              return (
                <Button
                  key={i}
                  onClick={a.onClick}
                  variant={a.primary ? 'default' : 'outline'}
                  size="lg"
                  className={a.primary ? 'bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5 flex-1 h-10 text-[13px]' : 'gap-1.5 flex-1 h-10 text-[13px]'}
                  data-testid={a.testId ?? `drawer-action-${i}`}
                >
                  {a.label}
                  <Icon className="w-3.5 h-3.5" />
                </Button>
              );
            })}
          </>
        ) : null
      }
    >
      {/* Top severity ribbon */}
      <motion.div
        initial={{ scaleX: 0, transformOrigin: 'left' }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className={`-mt-6 -mx-6 mb-1 h-0.5 bg-gradient-to-r ${ribbonGrad}`}
      />

      {/* Verdict hero block — shown when the record has a definitive read */}
      {verdict && <VerdictBlock verdict={verdict} />}

      {/* Subtitle paragraph */}
      {subtitle && idTag && !verdict && (
        <p className="text-[13.5px] text-apex-ink-2 leading-[1.65]">{subtitle}</p>
      )}

      {/* Interpretive signal — soft tinted callout beneath the verdict */}
      {signal && <SignalCallout signal={signal} />}

      {/* Meta — header strip + cards (title left, value right) */}
      {meta.length > 0 && (
        <section data-testid="drawer-meta">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-apex-lens" />
              <h4 className="text-[11px] uppercase tracking-[0.16em] font-bold text-apex-ink">Snapshot</h4>
            </div>
            {idTag && (
              <span className="text-[11px] font-mono text-apex-lens inline-flex items-center gap-1">
                <Hash className="w-3 h-3" />{idTag}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-2">
            {meta.map((m) => <MetaRow key={m.label} item={m} />)}
          </div>
        </section>
      )}

      {/* Sections */}
      {sections.map((s, i) => (
        <Section key={i} section={s} index={i} />
      ))}
    </DrawerPanel>
  );
}

// VerdictBlock — Wiz/Linear-style hero with icon-tile + label + sub
function VerdictBlock({ verdict }) {
  const tone = VERDICT_TONE[verdict.tone] ?? VERDICT_TONE.neutral;
  const Icon = verdict.icon ?? VERDICT_ICON[verdict.tone] ?? Activity;
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="flex items-start gap-3.5"
      data-testid="drawer-verdict"
    >
      <div className={`shrink-0 w-12 h-12 rounded-xl ring-1 flex items-center justify-center ${tone.tile}`}>
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <h3 className={`text-[20px] font-bold tracking-tight leading-tight ${tone.text}`}>{verdict.label}</h3>
        {verdict.sub && <p className="text-[13px] text-apex-neutral mt-1 leading-relaxed">{verdict.sub}</p>}
      </div>
    </motion.div>
  );
}

// SignalCallout — interpretive paragraph in a soft tinted card
function SignalCallout({ signal }) {
  const obj = typeof signal === 'string' ? { tone: 'neutral', body: signal } : signal;
  const tone = VERDICT_TONE[obj.tone] ?? VERDICT_TONE.neutral;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.12, duration: 0.4 }}
      className={`rounded-xl border ${tone.call} px-4 py-3.5`}
      data-testid="drawer-signal"
    >
      <p className="text-[13.5px] leading-[1.65] text-apex-ink">
        <span className={`font-semibold ${tone.accent}`}>{obj.lead ?? "What this signals:"} </span>
        {obj.body}
      </p>
    </motion.div>
  );
}

// MetaRow — title-left / value-right card with optional sub-text and tone color
function MetaRow({ item }) {
  const navigate = useNavigate();
  const toneColor = item.tone === 'critical' ? 'text-apex-critical'
                  : item.tone === 'success'  ? 'text-apex-success'
                  : item.tone === 'warning'  ? 'text-apex-warning'
                  : item.tone === 'lens'     ? 'text-apex-lens'
                  : 'text-apex-ink';
  const TrendIcon = item.delta === 'up' ? TrendingUp
                  : item.delta === 'down' ? TrendingDown
                  : item.delta === 'flat' ? Minus
                  : null;
  const pivot = item.pivot;
  const onPivot = pivot
    ? () => { if (typeof pivot === 'function') pivot(); else navigate(pivot.to ?? pivot); }
    : null;
  return (
    <div
      onClick={onPivot}
      role={onPivot ? 'button' : undefined}
      tabIndex={onPivot ? 0 : undefined}
      onKeyDown={onPivot ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPivot(); } } : undefined}
      data-testid={onPivot ? `pivot-meta-${(item.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : undefined}
      title={onPivot ? (pivot.label ?? `Open ${item.label}`) : undefined}
      className={`group relative rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-slate-300 transition-colors ${onPivot ? 'cursor-pointer hover:border-apex-lens/40 hover:shadow-sm' : ''}`}
    >
      {onPivot && (
        <div className="absolute right-0 top-2 bottom-2 w-0.5 rounded-l-full bg-apex-lens scale-y-0 origin-center group-hover:scale-y-100 transition-transform duration-200" />
      )}
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[12px] font-semibold text-apex-ink truncate">{item.label}</div>
          {item.sub && <div className="text-[11px] text-apex-neutral mt-0.5 leading-snug">{item.sub}</div>}
        </div>
        <div className={`shrink-0 inline-flex items-center gap-1.5 ${toneColor}`}>
          {TrendIcon && <TrendIcon className="w-3.5 h-3.5" />}
          <span className={`${item.mono ? 'font-mono' : 'font-semibold'} text-[15px] tabular leading-none`}>
            {item.value}
          </span>
          {onPivot && (
            <ArrowUpRight className="w-3.5 h-3.5 text-apex-lens opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title, icon: Icon }) {
  if (!title) return null;
  return (
    <div className="flex items-center gap-2 mb-3">
      {Icon ? (
        <Icon className="w-3.5 h-3.5 text-apex-lens" />
      ) : (
        <span className="w-1 h-3.5 rounded-full bg-apex-lens" />
      )}
      <h4 className="text-[12px] font-semibold text-apex-ink tracking-tight">{title}</h4>
    </div>
  );
}

function Section({ section, index = 0 }) {
  const {
    title, type = 'list', items = [], body = null, mono = false,
    chart, kpis, callout, icon,
  } = section;
  const slug = title?.toLowerCase().replace(/[^a-z]+/g, '-');
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.10 + index * 0.05, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`drawer-section-${slug}`}
      className="relative"
    >
      <SectionHeader title={title} icon={icon} />

      {type === 'paragraph' && body && (
        <p className={`text-[13.5px] leading-[1.7] ${mono ? 'font-mono text-apex-ink-2' : 'text-apex-ink-2'}`}>
          {body}
        </p>
      )}

      {type === 'list' && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[13px] text-apex-ink-2 leading-[1.6]">
              <span className="shrink-0 mt-[7px] w-1.5 h-1.5 rounded-full bg-apex-lens" />
              <span className={mono ? 'font-mono' : ''}>{it}</span>
            </li>
          ))}
        </ul>
      )}

      {type === 'kv' && items.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-[12px]">
            <tbody className="divide-y divide-slate-100">
              {items.map((it, i) => <KvRow key={i} it={it} />)}
            </tbody>
          </table>
        </div>
      )}

      {type === 'code' && body && (
        <pre className="rounded-lg bg-apex-shell text-slate-200 px-4 py-3 text-[12px] font-mono overflow-x-auto whitespace-pre-wrap break-words border border-slate-700">
          {body}
        </pre>
      )}

      {type === 'timeline' && items.length > 0 && (
        <ol className="relative border-l border-dashed border-slate-300 ml-2 space-y-3.5">
          {items.map((it, i) => (
            <li key={i} className="ml-4 relative">
              <span className={`absolute -left-[19px] top-1 w-2.5 h-2.5 rounded-full ${it.tone === 'critical' ? 'bg-apex-critical' : it.tone === 'warning' ? 'bg-apex-warning' : it.tone === 'success' ? 'bg-apex-success' : 'bg-slate-400'} ring-4 ring-white`} />
              <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-mono">{it.when}</div>
              <div className="text-[13px] text-apex-ink leading-snug mt-0.5">{it.label}</div>
              {it.detail && <div className="text-[11.5px] text-apex-neutral mt-0.5 font-mono">{it.detail}</div>}
            </li>
          ))}
        </ol>
      )}

      {type === 'callout' && callout && <Callout {...callout} />}

      {type === 'kpis' && Array.isArray(kpis) && kpis.length > 0 && (
        <div className="space-y-2">
          {kpis.map((k, i) => <KpiCard key={i} k={k} />)}
        </div>
      )}

      {type === 'chart' && chart && <ChartRouter chart={chart} />}
    </motion.section>
  );
}

// ChartRouter — selects the right chart variant based on chart.kind
function ChartRouter({ chart }) {
  const kind = chart.kind ?? 'paired';
  if (kind === 'paired')   return <PairedBarsChart {...chart} />;
  if (kind === 'area')     return <AreaThresholdChart {...chart} />;
  if (kind === 'heartbeat')return <HeartbeatGrid {...chart} />;
  if (kind === 'pulse')    return <PulseWaveform {...chart} />;
  if (kind === 'donut')    return <DonutChart {...chart} />;
  if (kind === 'burndown') return <BurndownChart {...chart} />;
  if (kind === 'progress') return <ProgressSteps {...chart} />;
  if (kind === 'stack')    return <StackedBar {...chart} />;
  if (kind === 'radial')   return <RadialBars {...chart} />;
  return <PairedBarsChart {...chart} />;
}

// KpiCard — title-left, big value-right with delta arrow + sub
function KpiCard({ k }) {
  const tone = k.tone === 'critical' ? 'text-apex-critical'
             : k.tone === 'success'  ? 'text-apex-success'
             : k.tone === 'warning'  ? 'text-apex-warning'
             : k.tone === 'lens'     ? 'text-apex-lens'
             : 'text-apex-ink';
  const TrendIcon = k.delta === 'up' ? TrendingUp
                  : k.delta === 'down' ? TrendingDown
                  : Minus;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/30 px-4 py-3.5 hover:bg-white hover:border-slate-300 transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-apex-ink leading-tight">{k.label}</div>
          {k.sub && <div className="text-[12px] text-apex-neutral mt-1 leading-snug">{k.sub}</div>}
        </div>
        <div className={`shrink-0 inline-flex items-center gap-1.5 ${tone}`}>
          <TrendIcon className="w-3.5 h-3.5" />
          <span className="font-bold tabular text-[18px] leading-none">{k.value}</span>
        </div>
      </div>
    </div>
  );
}

function Callout({ tone = 'neutral', title, body, lead }) {
  const t = VERDICT_TONE[tone] ?? VERDICT_TONE.neutral;
  return (
    <div className={`rounded-lg border ${t.call} px-4 py-3`}>
      {title && <div className={`text-[12px] font-semibold mb-1 ${t.accent}`}>{title}</div>}
      <p className="text-[13px] leading-[1.65] text-apex-ink">
        {lead && <span className={`font-semibold ${t.accent}`}>{lead} </span>}
        {body}
      </p>
    </div>
  );
}

// PairedBarsChart — clean SVG paired-bar chart for "A vs B per period"
//   chart: { labels: ['W1','W2',...], pairs: [{a, b}, ...], aLabel, bLabel, aColor, bColor }
function PairedBarsChart({ labels = [], pairs = [], aLabel = 'A', bLabel = 'B', aColor = '#DC2626', bColor = '#10B981' }) {
  if (!pairs.length) return null;
  const W = 480;
  const H = 180;
  const padX = 16;
  const padY = 18;
  const max = Math.max(1, ...pairs.flatMap((p) => [p.a, p.b]));
  const groupW = (W - padX * 2) / pairs.length;
  const barW = Math.min(28, groupW * 0.35);
  const gap = 4;
  const innerH = H - padY * 2;

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4" data-testid="drawer-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44">
        {/* Subtle horizontal guide lines */}
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line
            key={g}
            x1={padX} x2={W - padX}
            y1={padY + innerH - innerH * g}
            y2={padY + innerH - innerH * g}
            stroke="#E2E8F0"
            strokeDasharray="2 4"
            strokeWidth="1"
          />
        ))}

        {pairs.map((p, i) => {
          const cx = padX + groupW * i + groupW / 2;
          const aX = cx - barW - gap / 2;
          const bX = cx + gap / 2;
          const aH = innerH * (p.a / max);
          const bH = innerH * (p.b / max);
          const baseY = padY + innerH;
          return (
            <g key={i}>
              <motion.rect
                x={aX}
                width={barW}
                fill={aColor}
                rx="4"
                initial={{ y: baseY, height: 0 }}
                animate={{ y: baseY - aH, height: aH }}
                transition={{ delay: 0.15 + i * 0.06, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              />
              <motion.rect
                x={bX}
                width={barW}
                fill={bColor}
                rx="4"
                initial={{ y: baseY, height: 0 }}
                animate={{ y: baseY - bH, height: bH }}
                transition={{ delay: 0.20 + i * 0.06, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              />
              <text
                x={cx}
                y={H - 4}
                textAnchor="middle"
                className="fill-slate-500"
                fontSize="11"
                fontFamily="ui-sans-serif, system-ui"
              >
                {labels[i] ?? ''}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex items-center justify-center gap-5 text-[12px]">
        <span className="inline-flex items-center gap-1.5 text-apex-ink-2">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: aColor }} />
          {aLabel}
        </span>
        <span className="inline-flex items-center gap-1.5 text-apex-ink-2">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: bColor }} />
          {bLabel}
        </span>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// AreaThresholdChart — single filled area trend with a dashed SLA threshold
// line. Best for "exposure-over-time vs. budget" stories.
//   chart: { kind:'area', labels:['W-4',…], values:[2,5,9,18], threshold:7,
//            label:'Live exposure days', accent:'#DC2626', sublabel?: }
// ───────────────────────────────────────────────────────────────────────────
function AreaThresholdChart({ labels = [], values = [], threshold, label, sublabel, accent = '#DC2626' }) {
  if (!values.length) return null;
  const W = 480, H = 180, padX = 24, padTop = 22, padBot = 30;
  const innerH = H - padTop - padBot;
  const max = Math.max(threshold ?? 0, ...values) * 1.15 || 1;
  const stepX = (W - padX * 2) / Math.max(1, values.length - 1);
  const points = values.map((v, i) => [padX + stepX * i, padTop + innerH - innerH * (v / max)]);
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1][0]} ${padTop + innerH} L ${points[0][0]} ${padTop + innerH} Z`;
  const thresholdY = threshold != null ? padTop + innerH - innerH * (threshold / max) : null;

  const last = values[values.length - 1];
  const overThresh = threshold != null && last > threshold;

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4" data-testid="drawer-chart-area">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[13px] font-semibold text-apex-ink">{label}</div>
          {sublabel && <div className="text-[12px] text-apex-neutral mt-0.5">{sublabel}</div>}
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-apex-neutral font-mono">Latest</div>
          <div className="text-[20px] font-bold tabular leading-none" style={{ color: overThresh ? accent : '#0F172A' }}>{last}</div>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44">
        <defs>
          <linearGradient id="area-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.30" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Threshold band (above threshold tinted) */}
        {thresholdY != null && (
          <rect x={padX} y={padTop} width={W - padX * 2} height={thresholdY - padTop} fill={accent} fillOpacity="0.04" />
        )}

        {/* Threshold line */}
        {thresholdY != null && (
          <g>
            <line x1={padX} x2={W - padX} y1={thresholdY} y2={thresholdY} stroke={accent} strokeOpacity="0.55" strokeDasharray="4 4" strokeWidth="1.2" />
            <text x={W - padX - 4} y={thresholdY - 4} textAnchor="end" fontSize="10" fontFamily="ui-monospace, monospace" fill={accent} opacity="0.85">
              SLA · {threshold}
            </text>
          </g>
        )}

        {/* Area */}
        <motion.path d={areaPath} fill="url(#area-grad)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.5 }} />

        {/* Line */}
        <motion.path
          d={linePath}
          fill="none"
          stroke={accent}
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength="1"
          initial={{ strokeDasharray: '0 1' }}
          animate={{ strokeDasharray: '1 0' }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
        />

        {/* Dots */}
        {points.map(([x, y], i) => (
          <motion.circle
            key={i}
            cx={x} cy={y} r={i === points.length - 1 ? 4 : 2.5}
            fill="white" stroke={accent} strokeWidth={i === points.length - 1 ? 2.5 : 1.5}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.6 + i * 0.05 }}
          />
        ))}

        {/* X-axis labels */}
        {labels.map((l, i) => (
          <text key={i} x={padX + stepX * i} y={H - 8} textAnchor="middle" fontSize="12" fill="#64748B" fontFamily="ui-sans-serif, system-ui">
            {l}
          </text>
        ))}
      </svg>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// HeartbeatGrid — dot matrix where each dot is a day; received vs missed.
// Best for "heartbeat" / continuity stories (Missing Evidence).
//   chart: { kind:'heartbeat', days:42, gapStart:35, gapEnd:42, label, sub }
// ───────────────────────────────────────────────────────────────────────────
function HeartbeatGrid({ days = 42, gapStart, gapEnd, label, sublabel }) {
  const cells = Array.from({ length: days });
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4" data-testid="drawer-chart-heartbeat">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[12px] font-semibold text-apex-ink">{label}</div>
          {sublabel && <div className="text-[11px] text-apex-neutral mt-0.5">{sublabel}</div>}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-apex-neutral">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" />Received</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-apex-critical" />Missed</span>
        </div>
      </div>
      <div className="grid grid-cols-[repeat(21,minmax(0,1fr))] gap-[3px]">
        {cells.map((_, i) => {
          const inGap = (gapStart != null && i >= gapStart) && (gapEnd == null || i <= gapEnd);
          return (
            <motion.span
              key={i}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.008, duration: 0.18 }}
              className={`block aspect-square rounded-[2px] ${inGap ? 'bg-apex-critical' : 'bg-emerald-500/85'}`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-apex-neutral">
        <span>{days} days ago</span>
        <span>today</span>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// DonutChart — proportion breakdown with center label.
// Best for "where did this come from" stories (Container Governance).
//   chart: { kind:'donut', segments:[{label,value,color}], centerValue, centerSub }
// ───────────────────────────────────────────────────────────────────────────
function DonutChart({ segments = [], centerValue, centerSub }) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const R = 56, r = 38, cx = 80, cy = 80;
  let acc = 0;
  const arcs = segments.map((s) => {
    const start = acc;
    const end = acc + (s.value / total);
    acc = end;
    const sa = start * Math.PI * 2 - Math.PI / 2;
    const ea = end * Math.PI * 2 - Math.PI / 2;
    const large = (end - start) > 0.5 ? 1 : 0;
    const x1 = cx + R * Math.cos(sa), y1 = cy + R * Math.sin(sa);
    const x2 = cx + R * Math.cos(ea), y2 = cy + R * Math.sin(ea);
    const x3 = cx + r * Math.cos(ea), y3 = cy + r * Math.sin(ea);
    const x4 = cx + r * Math.cos(sa), y4 = cy + r * Math.sin(sa);
    const d = `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${r} ${r} 0 ${large} 0 ${x4} ${y4} Z`;
    return { ...s, d, pct: ((s.value / total) * 100).toFixed(0) };
  });
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4" data-testid="drawer-chart-donut">
      <div className="flex items-center gap-5">
        <svg viewBox="0 0 160 160" className="w-32 h-32 shrink-0">
          {arcs.map((a, i) => (
            <motion.path
              key={i}
              d={a.d}
              fill={a.color}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
              style={{ transformOrigin: '80px 80px' }}
            />
          ))}
          {/* Animated scan ring — sweeps once around to draw attention */}
          <motion.circle
            cx={cx} cy={cy} r={(R + r) / 2}
            fill="none"
            stroke="#0F172A"
            strokeOpacity="0.18"
            strokeWidth="2"
            strokeDasharray="6 240"
            initial={{ strokeDashoffset: 0 }}
            animate={{ strokeDashoffset: -250 }}
            transition={{ duration: 1.6, ease: 'easeInOut' }}
          />
          <text x={cx} y={cy - 2} textAnchor="middle" fontSize="22" fontWeight="700" fill="#0F172A" fontFamily="ui-sans-serif, system-ui">{centerValue}</text>
          {centerSub && <text x={cx} y={cy + 18} textAnchor="middle" fontSize="11" fill="#64748B" fontFamily="ui-monospace, monospace">{centerSub}</text>}
        </svg>
        <ul className="flex-1 space-y-1.5">
          {arcs.map((a, i) => (
            <li key={i} className="flex items-center justify-between gap-2 text-[12px]">
              <span className="inline-flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: a.color }} />
                <span className="text-apex-ink-2 truncate">{a.label}</span>
              </span>
              <span className="font-mono tabular text-apex-ink shrink-0">{a.pct}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// BurndownChart — actual vs ideal burn-down line. Best for "remaining work
// over time" stories (Remediation).
//   chart: { kind:'burndown', labels, actual:[…], ideal:[…], yLabel }
// ───────────────────────────────────────────────────────────────────────────
function BurndownChart({ labels = [], actual = [], ideal = [], yLabel = 'Remaining' }) {
  if (!actual.length) return null;
  const W = 480, H = 180, padX = 28, padTop = 22, padBot = 30;
  const innerH = H - padTop - padBot;
  const all = [...actual, ...ideal];
  const max = Math.max(...all) * 1.1 || 1;
  const n = actual.length;
  const stepX = (W - padX * 2) / Math.max(1, n - 1);
  const toPath = (arr) => arr.map((v, i) => `${i === 0 ? 'M' : 'L'} ${padX + stepX * i} ${padTop + innerH - innerH * (v / max)}`).join(' ');
  const aPath = toPath(actual), iPath = toPath(ideal);
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4" data-testid="drawer-chart-burndown">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[12px] font-semibold text-apex-ink">{yLabel}</div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="inline-flex items-center gap-1.5 text-apex-ink-2"><span className="w-3 h-0.5 rounded-full bg-apex-lens" />Actual</span>
          <span className="inline-flex items-center gap-1.5 text-apex-neutral"><span className="w-3 border-t-2 border-dashed border-slate-400" />Target</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44">
        {[0.25, 0.5, 0.75].map((g) => (
          <line key={g} x1={padX} x2={W - padX} y1={padTop + innerH * g} y2={padTop + innerH * g} stroke="#E2E8F0" strokeDasharray="2 4" strokeWidth="1" />
        ))}
        {/* Ideal line (dashed) */}
        <motion.path d={iPath} fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="4 4" pathLength="1" initial={{ strokeDasharray: '0 1' }} animate={{ strokeDasharray: '1 0' }} transition={{ duration: 0.8 }} />
        {/* Actual line */}
        <motion.path d={aPath} fill="none" stroke="#0EA5E9" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" pathLength="1" initial={{ strokeDasharray: '0 1' }} animate={{ strokeDasharray: '1 0' }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} />
        {actual.map((v, i) => {
          const x = padX + stepX * i;
          const y = padTop + innerH - innerH * (v / max);
          const isLast = i === actual.length - 1;
          if (isLast) {
            return (
              <g key={i}>
                <motion.circle cx={x} cy={y} r="11" fill="#0EA5E9" fillOpacity="0.18"
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: [0.7, 1.4, 0.7], opacity: [0.2, 0.5, 0.2] }}
                  transition={{ delay: 1.0, duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  style={{ transformOrigin: `${x}px ${y}px` }}
                />
                <motion.circle cx={x} cy={y} r="5" fill="white" stroke="#0EA5E9" strokeWidth="2.5"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} />
                <motion.circle cx={x} cy={y} r="2" fill="#0EA5E9"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.95 }} />
              </g>
            );
          }
          return (
            <motion.circle key={i} cx={x} cy={y} r="2.5" fill="white" stroke="#0EA5E9" strokeWidth="1.5"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 + i * 0.05 }} />
          );
        })}
        {labels.map((l, i) => (
          <text key={i} x={padX + stepX * i} y={H - 8} textAnchor="middle" fontSize="12" fill="#64748B" fontFamily="ui-sans-serif, system-ui">{l}</text>
        ))}
      </svg>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// ProgressSteps — horizontal pipeline with milestone dots; the dot color +
// fill indicate state (done/active/pending). Best for "steps to closure"
// stories (Investigations rem).
//   chart: { kind:'progress', steps:[{label, sub, state:'done'|'active'|'pending'}], current }
// ───────────────────────────────────────────────────────────────────────────
function ProgressSteps({ steps = [] }) {
  if (!steps.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-5 py-5" data-testid="drawer-chart-progress">
      <div className="relative">
        {/* Connector line */}
        <div className="absolute left-0 right-0 top-3 h-0.5 bg-slate-200" />
        <div className="grid" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0,1fr))` }}>
          {steps.map((s, i) => {
            const color =
              s.state === 'done'   ? '#10B981'
            : s.state === 'active' ? '#0EA5E9'
            :                        '#CBD5E1';
            const fill = s.state === 'done' ? color : 'white';
            return (
              <div key={i} className="flex flex-col items-center text-center px-1">
                <div className="relative z-10 w-7 h-7 flex items-center justify-center">
                  {/* Pulsing halo for the active step */}
                  {s.state === 'active' && (
                    <motion.span
                      className="absolute inset-0 rounded-full"
                      style={{ background: color, opacity: 0.22 }}
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: [0.6, 1.6, 0.6], opacity: [0.18, 0.0, 0.18] }}
                      transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  )}
                  <motion.div
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.1 + i * 0.08, duration: 0.35 }}
                    className="relative w-6 h-6 rounded-full border-2 flex items-center justify-center"
                    style={{ borderColor: color, background: fill }}
                  >
                    {s.state === 'done' && <span className="text-white text-[11px] font-bold leading-none">✓</span>}
                    {s.state === 'active' && <span className="w-2 h-2 rounded-full" style={{ background: color }} />}
                  </motion.div>
                </div>
                <div className={`mt-2 text-[12px] font-semibold leading-tight ${s.state === 'pending' ? 'text-apex-neutral' : 'text-apex-ink'}`}>{s.label}</div>
                {s.sub && <div className="text-[11px] text-apex-neutral mt-0.5 leading-snug">{s.sub}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// StackedBar — single horizontal bar split into colored segments with legend.
// Best for "share of total" stories (Change Legitimacy).
//   chart: { kind:'stack', segments:[{label,value,color}], total, label, sublabel }
// ───────────────────────────────────────────────────────────────────────────
function StackedBar({ segments = [], total, label, sublabel }) {
  const sum = segments.reduce((s, x) => s + x.value, 0);
  const denom = total ?? sum ?? 1;
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4" data-testid="drawer-chart-stack">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="text-[12px] font-semibold text-apex-ink">{label}</div>
          {sublabel && <div className="text-[11px] text-apex-neutral mt-0.5">{sublabel}</div>}
        </div>
        <div className="text-[11px] font-mono text-apex-neutral tabular">n = {denom.toLocaleString()}</div>
      </div>
      <div className="flex h-6 rounded-md overflow-hidden border border-slate-200">
        {segments.map((s, i) => (
          <motion.div
            key={i}
            title={`${s.label}: ${s.value}`}
            initial={{ width: 0 }}
            animate={{ width: `${(s.value / denom) * 100}%` }}
            transition={{ delay: 0.1 + i * 0.05, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            style={{ background: s.color }}
            className="h-full"
          />
        ))}
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
        {segments.map((s, i) => (
          <li key={i} className="flex items-center justify-between gap-2 text-[12px]">
            <span className="inline-flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: s.color }} />
              <span className="text-apex-ink-2 truncate">{s.label}</span>
            </span>
            <span className="font-mono tabular text-apex-ink shrink-0">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// PulseWaveform — EKG-style animated waveform.  Rhythmic peaks where evidence
// was received, fading into a flatline tail through the silence window. Best
// for Missing Evidence (replaces square grid).
//   chart: { kind:'pulse', label, sublabel, totalDays, gapDays }
// ───────────────────────────────────────────────────────────────────────────
function PulseWaveform({ label, sublabel, totalDays = 42, gapDays = 9 }) {
  const W = 480, H = 160;
  const padX = 16;
  const padY = 28;
  const innerW = W - padX * 2;
  const baseline = H - padY;

  // Build the EKG path. We model each "beat" as: small dip → sharp tall spike → settle.
  // Beats every ~4px until silence start, then flat.
  const beats = totalDays - gapDays;
  const beatWidth = innerW / totalDays;
  const cmds = [`M ${padX} ${baseline}`];
  for (let i = 0; i < beats; i++) {
    const x0 = padX + i * beatWidth;
    const x1 = x0 + beatWidth * 0.20;
    const x2 = x0 + beatWidth * 0.35;
    const x3 = x0 + beatWidth * 0.50;
    const x4 = x0 + beatWidth * 0.70;
    const x5 = x0 + beatWidth;
    const dipY  = baseline - 4;
    const peakY = baseline - 64;
    const undY  = baseline + 6;
    cmds.push(`L ${x1.toFixed(2)} ${baseline.toFixed(2)}`);
    cmds.push(`L ${x2.toFixed(2)} ${dipY.toFixed(2)}`);
    cmds.push(`L ${x3.toFixed(2)} ${peakY.toFixed(2)}`);
    cmds.push(`L ${x4.toFixed(2)} ${undY.toFixed(2)}`);
    cmds.push(`L ${x5.toFixed(2)} ${baseline.toFixed(2)}`);
  }
  // Flatline through the gap
  const flatStartX = padX + beats * beatWidth;
  cmds.push(`L ${(W - padX).toFixed(2)} ${baseline.toFixed(2)}`);
  const beatsPath = cmds.join(' ');

  // Gap region overlay
  const gapW = innerW * (gapDays / totalDays);

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-5 py-5" data-testid="drawer-chart-pulse">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[13px] font-semibold text-apex-ink">{label}</div>
          {sublabel && <div className="text-[12px] text-apex-neutral mt-0.5">{sublabel}</div>}
        </div>
        <div className="flex items-center gap-3 text-[12px]">
          <span className="inline-flex items-center gap-1.5 text-emerald-600"><span className="w-2 h-2 rounded-full bg-emerald-500" />Pulse</span>
          <span className="inline-flex items-center gap-1.5 text-apex-critical"><span className="w-2 h-2 rounded-full bg-apex-critical" />Silence</span>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40">
        <defs>
          <linearGradient id="pulse-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#10B981" />
            <stop offset={`${(beats / totalDays) * 100}%`} stopColor="#10B981" />
            <stop offset={`${(beats / totalDays) * 100}%`} stopColor="#DC2626" />
            <stop offset="100%" stopColor="#DC2626" />
          </linearGradient>
          <filter id="pulse-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.6" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Subtle grid */}
        {[0.25, 0.5, 0.75].map((g) => (
          <line key={g} x1={padX} x2={W - padX} y1={padY + (baseline - padY) * g} y2={padY + (baseline - padY) * g} stroke="#E2E8F0" strokeDasharray="2 4" strokeWidth="1" />
        ))}

        {/* Silence band */}
        <rect x={flatStartX} y={padY - 4} width={gapW} height={baseline - padY + 16} fill="#DC2626" fillOpacity="0.05" />
        <line x1={flatStartX} x2={flatStartX} y1={padY} y2={baseline + 6} stroke="#DC2626" strokeOpacity="0.45" strokeDasharray="3 3" strokeWidth="1.2" />

        {/* Animated EKG line — draw-in over 1.6s */}
        <motion.path
          d={beatsPath}
          fill="none"
          stroke="url(#pulse-grad)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#pulse-glow)"
          pathLength="1"
          initial={{ strokeDasharray: '0 1' }}
          animate={{ strokeDasharray: '1 0' }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        />

        {/* Pulsing dot at the silence boundary */}
        <motion.g>
          <motion.circle
            cx={flatStartX}
            cy={baseline}
            r="6"
            fill="#DC2626"
            fillOpacity="0.30"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: [0.6, 1.4, 0.6], opacity: [0.3, 0.6, 0.3] }}
            transition={{ delay: 1.6, duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            style={{ transformOrigin: `${flatStartX}px ${baseline}px` }}
          />
          <motion.circle
            cx={flatStartX}
            cy={baseline}
            r="3"
            fill="#DC2626"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6 }}
          />
        </motion.g>

        {/* Silence label */}
        <motion.text
          x={Math.min(W - padX - 8, flatStartX + Math.max(40, gapW / 2))}
          y={padY + 4}
          textAnchor={gapW < 90 ? 'end' : 'middle'}
          fontSize="12"
          fontFamily="ui-monospace, monospace"
          fontWeight="600"
          fill="#DC2626"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.95 }}
          transition={{ delay: 1.7, duration: 0.5 }}
        >
          {gapDays}-day silence
        </motion.text>
      </svg>

      <div className="flex items-center justify-between text-[11px] font-mono text-apex-neutral mt-1">
        <span>{totalDays} days ago</span>
        <span>today</span>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// RadialBars — concentric activity rings (Apple-Watch style). Each ring is
// one category; arc length = share of total. Best for Change Legitimacy
// (replaces the stack bar).
//   chart: { kind:'radial', label, sublabel, total, segments:[{label,value,color}] }
// ───────────────────────────────────────────────────────────────────────────
function RadialBars({ label, sublabel, total, segments = [] }) {
  const denom = total ?? segments.reduce((s, x) => s + x.value, 0) ?? 1;
  // sort smallest-first so smallest sit on the inside (most prominent)
  const ordered = [...segments].sort((a, b) => a.value - b.value);
  const cx = 90, cy = 90;
  const baseR = 28;
  const ringW = 9;
  const gap = 4;

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-5 py-5" data-testid="drawer-chart-radial">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[13px] font-semibold text-apex-ink">{label}</div>
          {sublabel && <div className="text-[12px] text-apex-neutral mt-0.5">{sublabel}</div>}
        </div>
        <div className="text-[12px] font-mono text-apex-neutral tabular">n = {denom.toLocaleString()}</div>
      </div>

      <div className="flex items-center gap-5">
        <svg viewBox="0 0 180 180" className="w-44 h-44 shrink-0">
          {ordered.map((s, i) => {
            const r = baseR + (ringW + gap) * i;
            const C = 2 * Math.PI * r;
            const pct = Math.max(0.005, s.value / denom); // ensure visible sliver for tiny segments
            return (
              <g key={i}>
                {/* Track */}
                <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F1F5F9" strokeWidth={ringW} />
                {/* Filled arc — animated */}
                <motion.circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={ringW}
                  strokeLinecap="round"
                  strokeDasharray={C}
                  initial={{ strokeDashoffset: C }}
                  animate={{ strokeDashoffset: C - C * pct }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
                  transform={`rotate(-90 ${cx} ${cy})`}
                />
              </g>
            );
          })}
          {/* Center label */}
          <text x={cx} y={cy - 2} textAnchor="middle" fontSize="22" fontWeight="700" fill="#0F172A" fontFamily="ui-sans-serif, system-ui">
            {denom.toLocaleString()}
          </text>
          <text x={cx} y={cy + 16} textAnchor="middle" fontSize="10" fill="#64748B" fontFamily="ui-monospace, monospace">
            changes · 30d
          </text>
        </svg>

        <ul className="flex-1 space-y-1.5 min-w-0">
          {ordered.slice().reverse().map((s, i) => (
            <li key={i} className="flex items-center justify-between gap-2 text-[12px] min-w-0">
              <span className="inline-flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: s.color }} />
                <span className="text-apex-ink-2 truncate">{s.label}</span>
              </span>
              <span className="font-mono tabular text-apex-ink shrink-0">
                {s.value} <span className="text-apex-neutral">· {((s.value / denom) * 100).toFixed(s.value < 10 ? 1 : 0)}%</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// KvRow — single row of the kv table; supports optional pivot navigation
function KvRow({ it }) {
  const navigate = useNavigate();
  const tone = it.tone === 'critical' ? 'text-apex-critical'
             : it.tone === 'success'  ? 'text-apex-success'
             : it.tone === 'warning'  ? 'text-apex-warning'
             : 'text-apex-ink-2';
  const onPivot = it.pivot
    ? () => { if (typeof it.pivot === 'function') it.pivot(); else navigate(it.pivot.to ?? it.pivot); }
    : null;
  return (
    <tr
      onClick={onPivot}
      data-testid={onPivot ? `pivot-kv-${(it.k || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : undefined}
      title={onPivot ? (it.pivot.label ?? `Open ${it.k}`) : undefined}
      role={onPivot ? 'button' : undefined}
      tabIndex={onPivot ? 0 : undefined}
      onKeyDown={onPivot ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPivot(); } } : undefined}
      className={`hover:bg-slate-50/60 transition-colors ${onPivot ? 'cursor-pointer hover:bg-apex-lens/5 focus:outline-none focus:bg-apex-lens/10' : ''}`}
    >
      <td className="px-3.5 py-2 text-apex-neutral font-mono w-1/2">{it.k}</td>
      <td className={`px-3.5 py-2 text-right font-mono ${tone}`}>
        <span className="inline-flex items-center justify-end gap-1.5">
          <span>{it.v}</span>
          {onPivot && <ArrowUpRight className="w-3 h-3 text-apex-lens" />}
        </span>
      </td>
    </tr>
  );
}

export { Section as DrawerSection };
